const selectors = {
  articles: '[data-blog-articles]',
  pagination: '[data-blog-pagination]',
  nextPage: '[data-blog-next-page]',
  feed: '[data-blog-feed]',
  tag: '[data-blog-tag]',
};

const observerOptions = {
  root: null,
  rootMargin: '240px 0px',
  threshold: 0,
};

const tagActiveClasses = ['button--primary'];
const tagInactiveClasses = ['button--outline'];

/**
 * Blog listing: tag filter + infinite scroll via Section Rendering API.
 */
export default class MainBlog {
  constructor(section) {
    this.el = section.el;
    this.sectionId = section.id;
    this.feed = this.el.querySelector(selectors.feed);
    this.baseUrl =
      this.el.querySelector('[data-blog-base-url]')?.dataset.blogBaseUrl || '';

    if (!this.feed) {
      return;
    }

    this.loading = false;
    this.observer = null;
    this.abortController = null;
    this.boundOnIntersect = this.onIntersect.bind(this);
    this.boundOnTagClick = this.onTagClick.bind(this);

    this.setupInfiniteScroll();
    this.setupTagFilters();
  }

  setupInfiniteScroll() {
    this.disconnectInfiniteScroll();
    this.list = this.el.querySelector(selectors.articles);
    this.paginationEl = this.el.querySelector(selectors.pagination);
    if (!this.list || !this.paginationEl) {
      return;
    }
    const trigger = this.paginationEl.querySelector(selectors.nextPage);
    if (!trigger) {
      return;
    }
    this.observer = new IntersectionObserver(
      this.boundOnIntersect,
      observerOptions,
    );
    this.observer.observe(this.paginationEl);
  }

  disconnectInfiniteScroll() {
    this.observer?.disconnect();
    this.observer = null;
  }

  onIntersect(entries) {
    const [entry] = entries;
    if (!entry?.isIntersecting || this.loading) {
      return;
    }
    const trigger = this.paginationEl?.querySelector(selectors.nextPage);
    if (!trigger) {
      return;
    }
    this.loadNext(trigger);
  }

  async loadNext(trigger) {
    if (!(trigger instanceof HTMLAnchorElement) || this.loading) {
      return;
    }

    let fetchUrl;
    try {
      fetchUrl = new URL(trigger.href);
    } catch {
      return;
    }

    this.loading = true;
    this.observer?.unobserve(this.paginationEl);

    this.abortController?.abort();
    this.abortController = new AbortController();

    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.abortController.signal,
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Blog section request failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newList = doc.querySelector(selectors.articles);
      const newPagination = doc.querySelector(selectors.pagination);

      if (newList) {
        newList.querySelectorAll(':scope > li').forEach(item => {
          this.list.appendChild(item);
        });
      }

      if (this.paginationEl) {
        if (newPagination) {
          this.paginationEl.innerHTML = newPagination.innerHTML;
          requestAnimationFrame(() => {
            if (
              this.paginationEl?.querySelector(selectors.nextPage) &&
              this.observer
            ) {
              this.observer.observe(this.paginationEl);
            } else {
              this.disconnectInfiniteScroll();
            }
          });
        } else {
          this.paginationEl.remove();
          this.paginationEl = null;
          this.disconnectInfiniteScroll();
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      if (this.paginationEl && this.observer) {
        this.observer.observe(this.paginationEl);
      }
    } finally {
      this.loading = false;
    }
  }

  setupTagFilters() {
    const tags = this.el.querySelectorAll(selectors.tag);
    if (!tags.length) {
      return;
    }
    tags.forEach(btn => {
      btn.addEventListener('click', this.boundOnTagClick);
    });
  }

  setTagButtonVisual(btn, active) {
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    tagActiveClasses.forEach(c => btn.classList.toggle(c, active));
    tagInactiveClasses.forEach(c => btn.classList.toggle(c, !active));
  }

  onTagClick(event) {
    if (this.loading) {
      return;
    }
    const clicked = event.currentTarget;
    if (!(clicked instanceof HTMLButtonElement)) {
      return;
    }

    const wasActive = clicked.getAttribute('aria-pressed') === 'true';
    const all = this.el.querySelectorAll(selectors.tag);

    all.forEach(btn => {
      this.setTagButtonVisual(btn, false);
    });

    if (!wasActive) {
      this.setTagButtonVisual(clicked, true);
    }

    const selected = this.el.querySelector(
      `${selectors.tag}[aria-pressed="true"]`,
    );
    const handle = selected?.dataset.blogTag;
    const path =
      handle && this.baseUrl
        ? `${this.baseUrl.replace(/\/$/, '')}/tagged/${encodeURIComponent(handle)}`
        : this.baseUrl || window.location.pathname;

    history.pushState({}, '', path);
    this.swapFeedFromSectionPath(path);
  }

  async swapFeedFromSectionPath(path) {
    if (!this.feed || this.loading) {
      return;
    }

    let fetchUrl;
    try {
      fetchUrl = new URL(path, window.location.origin);
    } catch {
      return;
    }

    this.loading = true;
    this.disconnectInfiniteScroll();

    this.abortController?.abort();
    this.abortController = new AbortController();
    fetchUrl.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(fetchUrl.toString(), {
        signal: this.abortController.signal,
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Blog section request failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newFeed = doc.querySelector(selectors.feed);

      if (newFeed) {
        this.feed.innerHTML = newFeed.innerHTML;
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
    } finally {
      this.loading = false;
      this.setupInfiniteScroll();
    }
  }

  onSectionUnload() {
    this.abortController?.abort();
    this.disconnectInfiniteScroll();
    this.el.querySelectorAll(selectors.tag).forEach(btn => {
      btn.removeEventListener('click', this.boundOnTagClick);
    });
  }
}
